import { isCreatorPagePath } from "./page-paths";
import type { PageAudience, PageNode } from "./pages";
import type { Customization } from "./customization";

// The runtime half of page updates (see page-updates.ts), with no schema library, so a creator
// site's script stays small.

export type PageUpdate = {
  id: string;
  title: string;
  body: string;
  /** Markup produced only by the shared rich text renderer after authorization. */
  story: string;
  locked: boolean;
  url: string | null;
  date: string | null;
  /** Authored public discovery fields, never an excerpt of a protected body. */
  summary?: string;
  image?: string | null;
  access?: "public" | "members";
  /** Synthetic presentation in an explicitly requested editor or template example. */
  example?: true;
};
/** `more`: an infinite feed has posts past these, which it asks `pageUpdatesMoreHref` for. */
export type PageUpdates = {
  items: PageUpdate[];
  canPublish: boolean;
  manageHref: string | null;
  more?: true;
};
/** A further page of an infinite feed's posts. */
export type PageUpdatesPage = { items: PageUpdate[]; more: boolean };

/** The private address of the posts after `last` in this feed, newest first. */
export function pageUpdatesMoreHref(
  node: Extract<PageNode, { kind: "updates" }>,
  last: Pick<PageUpdate, "id" | "date">,
) {
  const query = new URLSearchParams({ limit: String(node.limit), after: last.id });
  if (last.date) query.set("date", last.date);
  if (node.collectionId) query.set("collection", node.collectionId);
  if (node.access) query.set("access", node.access);
  return `/api/page-updates/posts?${query}`;
}

/** A trusted feed never carries a locked post's body or rendered story. */
export function pageUpdateProjection(input: PageUpdate): PageUpdate {
  return {
    id: input.id,
    title: input.title,
    body: input.locked ? "" : input.body,
    story: input.locked ? "" : input.story,
    locked: input.locked,
    url: input.url,
    date: input.date,
    ...(input.summary !== undefined ? { summary: input.summary } : {}),
    ...(input.image !== undefined ? { image: input.image } : {}),
    ...(input.access !== undefined ? { access: input.access } : {}),
    ...(input.example === true ? { example: true as const } : {}),
  };
}

export function validUpdateTimeZone(value: string) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function pageUpdateDate(date: string, node: Extract<PageNode, { kind: "updates" }>) {
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: node.timeZone,
    ...(node.dateFormat === "date-time" ? { hour: "numeric", minute: "2-digit" } : {}),
  });
}

/** Forms return to the authored page; an external address is never a redirect target. */
export function pageUpdateReturnPath(value: unknown) {
  return typeof value === "string" && isCreatorPagePath(value) ? value : "/";
}

/** A member landing page must actually exist; an unpublished choice falls back to Home. */
export function memberHomepagePath(
  header: Customization["header"],
  audience: PageAudience,
  available: readonly string[],
) {
  const path = header?.memberHomePath;
  if (!path || path === "/" || !isCreatorPagePath(path) || !available.includes(path)) return "/";
  return audience === "paid" || (audience === "free" && header?.memberHomeAudience === "signed-in")
    ? path
    : "/";
}
